'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2LinkPoint, PrV2LinkTarget, PrV2SymbolPeekResolution } from '@/lib/pr-v2/code-changes/symbol-peek';
import type { PrV2RepoFileSide } from '@/lib/pr-v2/code-changes/types';
import type { CodeContent } from '@/types/github';
import { useQueryClient } from '@tanstack/react-query';
import type { RefObject } from 'react';
import { useEffect, useEffectEvent } from 'react';
import { toast } from 'sonner';
import type { PrV2RepoFileLineHit, PrV2RepoFileLineSource } from '../lib/pr-v2-repo-file-line';
import { createPrV2RepoFileLineRange, findPrV2RepoFileLineHit } from '../lib/pr-v2-repo-file-line';
import { hydrateOmittedCodeContents } from '@/data/v2/pr/hydrate-omitted-code-contents';
import { ensurePrV2RepoFile } from '@/data/v2/pr/use-pr-v2-repo-file';
import { findPrV2LinkTarget, resolvePrV2SymbolPeek } from '@/lib/pr-v2/code-changes/symbol-peek';
import { getPrV2ActiveFileStoreKey } from '@/lib/stores/navigation/pr-v2-active-file-store';
import { usePrV2RepoFileStore } from '@/lib/stores/pr-v2-repo-file-store';
import { metaChar } from '@/lib/utils';
const LINK_HIGHLIGHT = 'pr-v2-repo-file-link';
const ARMED_LINK_HIGHLIGHT = 'pr-v2-repo-file-link-armed';

/**
 * The underline's styles. They reach Pierre's text only from inside its shadow root, through
 * `unsafeCSS`, and only as longhands: Chromium ignores the `text-decoration` shorthand in
 * `::highlight()`.
 */
export const PR_V2_REPO_FILE_LINK_UNSAFE_CSS = `
::highlight(${LINK_HIGHLIGHT}) {
  text-decoration-line: underline;
  text-decoration-style: dotted;
}

::highlight(${ARMED_LINK_HIGHLIGHT}) {
  text-decoration-line: underline;
  text-decoration-style: solid;
}
`;
type PrV2RepoFileLinksOptions = PrV2RepoFileLineSource & {
  owner: string;
  repo: string;
  number: string;
  headSha: string | null;
  baseSha: string | null;
  /** The pull request's changed files, searched for a definition the clicked file lacks. */
  changedFiles: readonly CodeContent[];
  /** This file's full text on each side, or null while the page does not hold it. */
  fileTexts: Record<PrV2RepoFileSide, string | null>;
};
type LinkHover = {
  host: HTMLElement;
  armed: boolean;
  /** The hovered path's or name's characters, or null while the pointer is on neither. */
  link: {
    kind: PrV2LinkTarget['kind'];
    range: Range;
  } | null;
};

/** A link the pointer was on, and the diff line it was found on. */
type LineLink = {
  line: Element;
  target: PrV2LinkTarget;
};

/** A cmd-clicked name's lookup ends in its resolution, or fails when a file cannot be read. */
type SymbolLookupOutcome = PrV2SymbolPeekResolution | {
  kind: 'failed';
};

/** One pointer hovers at most one link on the page, so every diff shares this state. */
let hover: LinkHover | null = null;

/**
 * The cmd-clicked name still being looked up, whose diff shows a progress cursor meanwhile. Each
 * click replaces it, so a lookup lands only while it is still this object.
 */
let pendingLookup: {
  host: HTMLElement;
} | null = null;

/**
 * Makes the paths and names in a Pierre diff openable. A cmd-click (ctrl-click off a Mac) on a
 * path opens it in the repository file panel; on a name, it opens the file that defines the name
 * at its definition. Hovering a path, or a name a click is known to open, underlines it, and
 * holding the modifier underlines any other name too. Both read the composed path, never Pierre's
 * token callbacks, which turn its token transformer on for every row of every diff. So the host
 * can be any element around the diff that stays mounted while the diff collapses and expands, such
 * as the file's section.
 */
export function usePrV2RepoFileLinks(hostRef, options) {
  const $ = _c(23);
  const {
    owner,
    repo,
    number,
    headSha,
    baseSha,
    changedFiles,
    fileTexts,
    filename,
    previousFilename,
    codeViewSide
  } = options;
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== fileTexts) {
    t0 = hit => findPrV2LinkTarget(toLinkPoint(hit, fileTexts));
    $[0] = fileTexts;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const findLinkAt = useEffectEvent(t0);
  let t1;
  if ($[2] !== baseSha || $[3] !== changedFiles || $[4] !== fileTexts || $[5] !== headSha || $[6] !== number || $[7] !== owner || $[8] !== queryClient || $[9] !== repo) {
    t1 = (host, hit_0, target) => {
      const prKey = getPrV2ActiveFileStoreKey(owner, repo, number);
      const text = hit_0.text.slice(target.range.start, target.range.end);
      if (target.kind === "path") {
        setPendingLookup(null);
        usePrV2RepoFileStore.getState().openRepoFile(prKey, {
          file: {
            specifier: text,
            fromPath: hit_0.fromPath,
            side: hit_0.side
          },
          line: null
        });
        return;
      }
      const lookup = {
        host
      };
      setPendingLookup(lookup);
      resolvePrV2SymbolPeek({
        ...toLinkPoint(hit_0, fileTexts),
        name: text,
        readFile: request => ensurePrV2RepoFile(queryClient, owner, repo, number, request, {
          headSha,
          baseSha
        }),
        changedFiles,
        loadChangedFiles: () => hydrateOmittedCodeContents(queryClient, owner, repo, number, [...changedFiles], {
          headSha,
          baseSha,
          shouldHydrateFile: file => file.filename !== hit_0.fromPath && file.previous_filename !== hit_0.fromPath
        })
      }).catch(_temp).then(outcome => {
        if (pendingLookup === lookup) {
          setPendingLookup(null);
          showSymbolLookupOutcome(prKey, text, outcome);
        }
      });
    };
    $[2] = baseSha;
    $[3] = changedFiles;
    $[4] = fileTexts;
    $[5] = headSha;
    $[6] = number;
    $[7] = owner;
    $[8] = queryClient;
    $[9] = repo;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const openLink = useEffectEvent(t1);
  let t2;
  if ($[11] !== codeViewSide || $[12] !== filename || $[13] !== findLinkAt || $[14] !== hostRef || $[15] !== openLink || $[16] !== previousFilename) {
    t2 = () => {
      const host_0 = hostRef.current;
      if (!host_0) {
        return;
      }
      const source = {
        filename,
        previousFilename,
        codeViewSide
      };
      const handleClick = event => {
        if (!isArmed(event)) {
          return;
        }
        const hit_1 = findPrV2RepoFileLineHit(event, source);
        const target_0 = hit_1 && findLinkAt(hit_1);
        if (!hit_1 || !target_0) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        openLink(host_0, hit_1, target_0);
      };
      let lastLink = null;
      const handlePointerMove = event_0 => {
        if (event_0.pointerType !== "mouse") {
          return;
        }
        const hit_2 = findPrV2RepoFileLineHit(event_0, source);
        const target_1 = hit_2 && (isOnLink(lastLink, hit_2) ? lastLink.target : findLinkAt(hit_2));
        lastLink = hit_2 && target_1 ? {
          line: hit_2.line,
          target: target_1
        } : null;
        updateLinkHover(host_0, hit_2, target_1, isArmed(event_0));
      };
      const handlePointerLeave = () => clearLinkHover(host_0);
      const hostObserver = new MutationObserver(() => clearDetachedLinkHover(host_0));
      host_0.addEventListener("click", handleClick, {
        capture: true
      });
      host_0.addEventListener("pointermove", handlePointerMove);
      host_0.addEventListener("pointerleave", handlePointerLeave);
      hostObserver.observe(host_0, {
        childList: true,
        subtree: true
      });
      return () => {
        host_0.removeEventListener("click", handleClick, {
          capture: true
        });
        host_0.removeEventListener("pointermove", handlePointerMove);
        host_0.removeEventListener("pointerleave", handlePointerLeave);
        hostObserver.disconnect();
        clearLinkHover(host_0);
        if (!host_0.isConnected && pendingLookup?.host === host_0) {
          setPendingLookup(null);
        }
      };
    };
    $[11] = codeViewSide;
    $[12] = filename;
    $[13] = findLinkAt;
    $[14] = hostRef;
    $[15] = openLink;
    $[16] = previousFilename;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  let t3;
  if ($[18] !== codeViewSide || $[19] !== filename || $[20] !== hostRef || $[21] !== previousFilename) {
    t3 = [codeViewSide, filename, hostRef, previousFilename];
    $[18] = codeViewSide;
    $[19] = filename;
    $[20] = hostRef;
    $[21] = previousFilename;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  useEffect(t2, t3);
}

/** The hovered and the clicked point both come from here, so a click looks up what the hover showed. */
function _temp() {
  return {
    kind: "failed"
  };
}
function toLinkPoint(hit: PrV2RepoFileLineHit, fileTexts: Record<PrV2RepoFileSide, string | null>): PrV2LinkPoint {
  return {
    lineText: hit.text,
    offset: hit.offset,
    fromPath: hit.fromPath,
    side: hit.side,
    fileText: fileTexts[hit.side]
  };
}
function isOnLink(link: LineLink | null, hit: PrV2RepoFileLineHit): link is LineLink {
  return link?.line === hit.line && link.target.range.start <= hit.offset && hit.offset < link.target.range.end;
}
function showSymbolLookupOutcome(prKey: string, name: string, outcome: SymbolLookupOutcome): void {
  switch (outcome.kind) {
    case 'peek':
      usePrV2RepoFileStore.getState().openRepoFile(prKey, outcome.peek);
      return;
    case 'external':
      toast(`${name} comes from ${outcome.specifier}`);
      return;
    case 'not-found':
      toast(`No definition found for ${name}`);
      return;
    case 'failed':
      toast(`Couldn't look up ${name}`);
  }
}
function updateLinkHover(host: HTMLElement, hit: PrV2RepoFileLineHit | null, target: PrV2LinkTarget | null, armed: boolean): void {
  const range = hit && target ? createPrV2RepoFileLineRange(hit.line, target.range) : null;
  const link = target && range ? {
    kind: target.kind,
    range
  } : null;

  // Most moves stay on one link or off every link, so the highlight is rebuilt only on a change.
  if (hover?.host === host && hover.armed === armed && isSameLink(hover.link, link)) {
    return;
  }
  setHover({
    host,
    armed,
    link
  });
}

/** Clears the hover when it is on `host`, as the pointer leaves the diff or the diff unmounts. */
function clearLinkHover(host: HTMLElement): void {
  if (hover?.host === host) {
    setHover(null);
  }
}

/** Clears the hover when it is on `host` and its path left the page, as when the diff collapses. */
function clearDetachedLinkHover(host: HTMLElement): void {
  if (hover?.host === host && hover.link && !hover.link.range.startContainer.isConnected) {
    setHover(null);
  }
}

/**
 * The only writer of the hover. The highlight, the host's tooltip, and the window listeners all
 * follow from it: a path, or a name a click is known to open, is dotted with a tooltip naming the
 * click, and a path or name is solid with a pointer cursor while that click's modifier is held. Any
 * other name shows nothing until then, so a word the click cannot open never looks like a link, but
 * its listeners still wait for the modifier.
 */
function setHover(next: LinkHover | null): void {
  const previousHost = hover?.host;
  previousHost?.removeAttribute('title');
  hover = next;
  syncCursor(previousHost);
  syncCursor(next?.host);
  const highlights = typeof Highlight === 'function' && 'highlights' in CSS ? CSS.highlights : null;
  highlights?.delete(LINK_HIGHLIGHT);
  highlights?.delete(ARMED_LINK_HIGHLIGHT);
  if (!next?.link) {
    window.removeEventListener('keydown', handleModifierKey);
    window.removeEventListener('keyup', handleModifierKey);
    window.removeEventListener('blur', handleWindowBlur);
    return;
  }
  if (next.armed) {
    highlights?.set(ARMED_LINK_HIGHLIGHT, new Highlight(next.link.range));
  } else if (next.link.kind !== 'name') {
    highlights?.set(LINK_HIGHLIGHT, new Highlight(next.link.range));
    next.host.title = `${metaChar()}-click to open`;
  }

  // The modifier can go down or up while the pointer rests on the link, and a window that loses
  // focus never hears the key come up.
  window.addEventListener('keydown', handleModifierKey);
  window.addEventListener('keyup', handleModifierKey);
  window.addEventListener('blur', handleWindowBlur);
}
function setPendingLookup(next: {
  host: HTMLElement;
} | null): void {
  const previousHost = pendingLookup?.host;
  pendingLookup = next;
  syncCursor(previousHost);
  syncCursor(next?.host);
}

/** Progress while the host's clicked name is looked up, else a pointer over an armed link. */
function syncCursor(host: HTMLElement | null | undefined): void {
  if (!host) {
    return;
  }
  if (pendingLookup?.host === host) {
    host.style.cursor = 'progress';
  } else if (hover?.host === host && hover.armed && hover.link) {
    host.style.cursor = 'pointer';
  } else {
    host.style.removeProperty('cursor');
  }
}
function handleModifierKey(event: KeyboardEvent): void {
  const armed = isArmed(event);
  if (hover && hover.armed !== armed) {
    setHover({
      ...hover,
      armed
    });
  }
}
function handleWindowBlur(): void {
  setHover(null);
}

/** Cmd on a Mac, where a ctrl-click opens the context menu, and ctrl elsewhere. */
function isArmed(event: MouseEvent | KeyboardEvent): boolean {
  return metaChar() === '⌘' ? event.metaKey : event.ctrlKey;
}
function isSameLink(left: LinkHover['link'], right: LinkHover['link']): boolean {
  return left === right || left !== null && right !== null && left.kind === right.kind && left.range.startContainer === right.range.startContainer && left.range.startOffset === right.range.startOffset && left.range.endContainer === right.range.endContainer && left.range.endOffset === right.range.endOffset;
}